import { CustomerEventType } from "@redotech/redo-customer-event-common/customer-event-definition";

export interface BaseStep {
  id: string;
  customTitle?: string;
}

/**
 * Keep in sync with the same enum in redo/temporal/workflows/run-automation/automation-activity-proxies
 * */
export enum StepType {
  // Marketing
  TRIGGER = "trigger", // check redo/temporal/workflows/run-automation.ts if this ever changes, it hardcodes "trigger"
  CONDITION = "condition",
  WAIT = "wait",
  WAIT_FOR_EVENT = "wait_for_event",
  DO_NOTHING = "do_nothing",
  SEND_EMAIL = "send_email",
  SEND_SMS = "send_sms",
  SEND_WEBHOOK = "send_webhook",
  AB_TEST = "ab_test",
  MANAGE_CUSTOMER_TAGS = "manage_customer_tags",
  MANAGE_STATIC_SEGMENT = "manage_static_segment",
  RECOVER = "recover",
  SHOPPER_AGENT_MESSAGE = "shopper_agent_message",

  // 1CU
  UPSELL = "upsell",
  DOWNSELL = "downsell",
  UPSELL_CONDITION = "upsell_condition",

  // OMS
  OMS_ACTION = "oms_action",
  OMS_CONDITION = "oms_condition",
  OMS_CODE = "oms_code",

  // Checkout personalization
  RCO_SPLIT = "rco_split",
  CHECKOUT_EXPERIENCE = "checkout_experience",
  RCO_SEGMENT = "rco_segment",
  BLOCK_CHECKOUT = "block_checkout",

  // Checkout packages
  RCO_CHECKOUT_PACKAGE = "rco_checkout_package",
  RCO_CHECKOUT_PACKAGE_CONDITION = "rco_checkout_package_condition",

  // Post-purchase flows
  POST_PURCHASE_PAGE = "post_purchase_page",
  POST_PURCHASE_SEGMENT = "post_purchase_segment",
  POST_PURCHASE_SPLIT = "post_purchase_split",

  // Tracking page flows
  TRACKING_PAGE = "tracking_page",

  // Support rules
  RULE_CONDITIONS = "rule_conditions",
  RULE_ACTIONS = "rule_actions",
  RULE_CONDITIONS_V3 = "rule_conditions_v3",
  RULE_ACTIONS_V3 = "rule_actions_v3",

  // Work order flows
  WORK_ORDER_WORK_STEP = "work_order_work_step",
  WORK_ORDER_CONDITION = "work_order_condition",

  // Return processing flows
  PROCESS_RETURN = "process_return",

  // Compensation and restocking flows (RET2-1527 follow-up)
  COMPENSATION_STEP = "compensation_step",
  RESTOCKING_STEP = "restocking_step",
  // Branch step for Compensation flows: wraps the existing return-flow
  // `Condition` discriminated union so the compensation worker can evaluate
  // it directly via the shared `Condition.evaluate(...)` runtime.
  RETURN_FLOW_CONDITION = "return_flow_condition",

  // Order tracking automations
  CONCIERGE_BLOCK = "concierge_block",

  // HSA questionnaire flow view: synthesized at read time from the
  // hsa_questionnaire_flow JSONB (CRDB), never persisted to AdvancedFlow.
  HSA_QUESTION_GROUP = "hsa_question_group",
  HSA_CONDITION = "hsa_condition",
  HSA_KNOCKOUT = "hsa_knockout",
  HSA_CONTENT = "hsa_content",
  HSA_END = "hsa_end",

  // Supplies shipping rules: branch on the cart, terminate in a rate.
  SUPPLIES_SHIPPING_CONDITION = "supplies_shipping_condition",
  SUPPLIES_SHIPPING_RATE = "supplies_shipping_rate",
}

/** What a supplies shipping condition branches on. */
export enum SuppliesShippingConditionKind {
  CART_VALUE = "cart_value",
  FIRST_TIME_CUSTOMER = "first_time_customer",
  AUTOMATED_REORDER = "automated_reorder",
  MERCHANT = "merchant",
  CART_ITEMS = "cart_items",
  PRODUCT_FAMILY_TAGS = "product_family_tags",
  DESTINATION_PROVINCE = "destination_province",
}

/** Shown when a supplies shipping rate step leaves its label blank. */
export const DEFAULT_SUPPLIES_SHIPPING_LABEL = "Shipping";

/** How a supplies shipping rate is expressed. */
export enum SuppliesShippingRateMode {
  FIXED = "fixed",
  PERCENT_OF_CART = "percent_of_cart",
}

/** Whether a list-valued supplies condition includes or excludes its values. */
export enum SuppliesShippingListOperator {
  ANY_OF = "any_of",
  NONE_OF = "none_of",
}

/** Comparison used by numeric supplies shipping conditions. */
export enum SuppliesShippingNumberOperator {
  LESS_THAN = "less_than",
  LESS_THAN_OR_EQUAL = "less_than_or_equal",
  EQUAL = "equal",
  GREATER_THAN = "greater_than",
  GREATER_THAN_OR_EQUAL = "greater_than_or_equal",
  /** Inclusive both ends; pairs `amountMinorUnits` with `maxAmountMinorUnits`. */
  BETWEEN = "between",
}

export const supportedWaitForEventTypes = [
  CustomerEventType.MARKETING_CONFIRMED,
] as const;

export type SupportedWaitForEventType =
  (typeof supportedWaitForEventTypes)[number];
export const SupportedWaitForEventType = supportedWaitForEventTypes;

export enum AutomationFilter {
  Status = "Status",
  Channel = "Channel",
  Category = "Category",
}

export enum AutomationStatus {
  Active = "Active",
  Inactive = "Inactive",
}

export enum AutomationChannel {
  Email = "Email",
  SMS = "SMS",
}

export enum AutomationSortKey {
  Name = "name",
  UpdatedAt = "updatedAt",
  Index = "index",
}

export const stepTypeDisplayNames: Partial<Record<StepType, string>> = {
  [StepType.DO_NOTHING]: "End",
  [StepType.WAIT]: "Wait",
  [StepType.WAIT_FOR_EVENT]: "Wait for event",
  [StepType.CONDITION]: "Branch",
  [StepType.SEND_SMS]: "Send SMS",
  [StepType.SEND_EMAIL]: "Send email",
  [StepType.SEND_WEBHOOK]: "Send webhook",
  [StepType.MANAGE_CUSTOMER_TAGS]: "Manage customer tags",
  [StepType.MANAGE_STATIC_SEGMENT]: "Manage static segments",
  [StepType.TRIGGER]: "Trigger",
  [StepType.UPSELL]: "Upsell",
  [StepType.DOWNSELL]: "Downsell",
  [StepType.UPSELL_CONDITION]: "Condition",
  [StepType.OMS_ACTION]: "Action",
  [StepType.OMS_CONDITION]: "Condition",
  [StepType.AB_TEST]: "Weighted branch",
  [StepType.RCO_SPLIT]: "A/B Test",
  [StepType.CHECKOUT_EXPERIENCE]: "Checkout experience",
  [StepType.RCO_SEGMENT]: "Segment",
  [StepType.BLOCK_CHECKOUT]: "Block checkout",
  [StepType.RCO_CHECKOUT_PACKAGE]: "Package",
  [StepType.RCO_CHECKOUT_PACKAGE_CONDITION]: "Condition",
  [StepType.POST_PURCHASE_PAGE]: "Post-purchase page",
  [StepType.POST_PURCHASE_SEGMENT]: "Post-purchase segment",
  [StepType.POST_PURCHASE_SPLIT]: "A/B Test",
  [StepType.RECOVER]: "Recover",
  [StepType.SHOPPER_AGENT_MESSAGE]: "Start Agent Conversation",
  [StepType.TRACKING_PAGE]: "Tracking page",
  [StepType.RULE_CONDITIONS]: "Conditions",
  [StepType.RULE_ACTIONS]: "Actions",
  [StepType.RULE_CONDITIONS_V3]: "Conditions",
  [StepType.RULE_ACTIONS_V3]: "Actions",
  [StepType.WORK_ORDER_WORK_STEP]: "Work step",
  [StepType.WORK_ORDER_CONDITION]: "Condition",
  [StepType.PROCESS_RETURN]: "Process return",
  [StepType.COMPENSATION_STEP]: "Issue compensation",
  [StepType.RESTOCKING_STEP]: "Restocking",
  [StepType.RETURN_FLOW_CONDITION]: "Condition",
  [StepType.HSA_QUESTION_GROUP]: "Question group",
  [StepType.HSA_CONDITION]: "Condition",
  [StepType.HSA_KNOCKOUT]: "Knockout",
  [StepType.HSA_CONTENT]: "Content screen",
  [StepType.HSA_END]: "Survey complete",
  [StepType.SUPPLIES_SHIPPING_CONDITION]: "Condition",
  [StepType.SUPPLIES_SHIPPING_RATE]: "Set shipping cost",
};
